import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFrameworksApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { isPlatformAdmin } from '../utils/adminAccess';
import { useToast } from '../hooks/useToast';
import { useSwal } from '../hooks/useSwal';
import { NimbusTable } from '../components/NimbusTable';
import { PageHeader } from '../components/PageHeader';

type FrameworkControl = {
  controlId: string;
  title: string;
  description?: string;
};

type Framework = {
  frameworkId: string;
  name: string;
  category: string;
  version?: string;
  code?: string;
  description?: string;
  createdAt?: string;
  controls?: FrameworkControl[];
};

const parseCsvControls = (text: string): FrameworkControl[] | null => {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length < 2) return null;

  const headers = lines[0]
    .split(',')
    .map((h) => h.trim().replace(/"/g, ''));

  const controlIdIdx = headers.findIndex((h) => h.toLowerCase() === 'control_id');
  const titleIdx = headers.findIndex((h) => h.toLowerCase() === 'control_title');
  const descIdx = headers.findIndex((h) => h.toLowerCase() === 'control_description');

  if (controlIdIdx === -1 || titleIdx === -1) return null;

  const controls: FrameworkControl[] = [];
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i]
      .split(',')
      .map((v) => v.trim().replace(/"/g, ''));

    const controlId = values[controlIdIdx] || '';
    if (!controlId) continue;

    controls.push({
      controlId,
      title: values[titleIdx] || '',
      description: descIdx !== -1 ? values[descIdx] || '' : '',
    });
  }

  return controls;
};

export const AdminFrameworksPage: React.FC = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const swal = useSwal();
  const isAdmin = isPlatformAdmin(user?.email || '');

  const { data: frameworks = [], isLoading, error } = useQuery<Framework[]>({
    queryKey: ['adminFrameworks'],
    queryFn: () => adminFrameworksApi.getAll(),
    enabled: !!user && isAdmin,
  });

  const [expandedFrameworkId, setExpandedFrameworkId] = useState<string | null>(null);

  const [form, setForm] = useState({
    name: '',
    category: '',
    version: '',
    code: '',
    description: '',
  });
  const [csvFile, setCsvFile] = useState<File | null>(null);

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!csvFile) throw new Error('CSV required');
      const csvText = await csvFile.text();
      const controls = parseCsvControls(csvText);
      if (!controls || controls.length === 0) throw new Error('Invalid CSV');

      await adminFrameworksApi.create({
        name: form.name,
        category: form.category,
        version: form.version,
        code: form.code,
        description: form.description,
        controls,
      });
    },
    onSuccess: async () => {
      setForm({ name: '', category: '', version: '', code: '', description: '' });
      setCsvFile(null);
      await qc.invalidateQueries({ queryKey: ['adminFrameworks'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (fw: Framework) => {
      await adminFrameworksApi.delete(fw.frameworkId);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['adminFrameworks'] });
      toast.success('Framework deleted.');
    },
    onError: (e: Error) => toast.error(`Failed to delete: ${e.message}`),
  });

  const handleDelete = async (fw: Framework) => {
    const ok = await swal.confirmDelete(fw.name);
    if (!ok) return;
    deleteMutation.mutate(fw);
  };

  const frameworkCountLabel = useMemo(() => {
    const count = frameworks.length;
    return `${count} framework${count === 1 ? '' : 's'}`;
  }, [frameworks.length]);

  if (!isAdmin) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y np-page">
        <div className="np-banner np-tone-warning">
          <i className="ri-error-warning-line"></i>
          <span>Access denied. Admin privileges required.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader icon="ri-stack-line" title="Framework Management" />

      <div className="np-card">
        <div className="np-card-head">
          <h2>Upload Framework</h2>
        </div>
        <div className="np-card-body">
          <div className="row g-3">
            <div className="col-12 col-md-6 np-field">
              <label>Framework Name</label>
              <input
                className="np-input"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="ISO 27001:2022"
              />
            </div>
            <div className="col-12 col-md-6 np-field">
              <label>Category</label>
              <select
                className="np-select"
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              >
                <option value="">Select category</option>
                <option value="Information Security">Information Security</option>
                <option value="Compliance & Audit">Compliance & Audit</option>
                <option value="Privacy & Data Protection">Privacy & Data Protection</option>
                <option value="Governance & Quality">Governance & Quality</option>
                <option value="Government & Cloud">Government & Cloud</option>
              </select>
            </div>
            <div className="col-12 col-md-3 np-field">
              <label>Version</label>
              <input className="np-input" value={form.version} onChange={(e) => setForm((f) => ({ ...f, version: e.target.value }))} placeholder="2022" />
            </div>
            <div className="col-12 col-md-3 np-field">
              <label>Short Code</label>
              <input className="np-input" value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} placeholder="ISO27001" />
            </div>
            <div className="col-12 col-md-6 np-field">
              <label>CSV File</label>
              <input
                type="file"
                className="np-input"
                accept=".csv"
                onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
              />
              <p className="np-hint">Required columns: control_id, control_title, control_description</p>
            </div>
            <div className="col-12 np-field mb-0">
              <label>Description</label>
              <textarea
                className="np-textarea"
                rows={2}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>
          </div>

          <div className="mt-3">
            <button
              className="np-btn np-btn-primary"
              onClick={() => createMutation.mutate()}
              disabled={createMutation.isPending || !form.name.trim() || !form.category || !csvFile}
            >
              {createMutation.isPending ? 'Uploading…' : 'Upload Framework'}
            </button>
          </div>

          {createMutation.error ? (
            <div className="np-banner np-tone-danger mt-3 mb-0">
              <i className="ri-error-warning-line"></i>
              <span>Upload failed. Ensure CSV includes required columns.</span>
            </div>
          ) : null}
        </div>
      </div>

      <div className="np-card">
        <div className="np-card-head">
          <h2>Frameworks</h2>
          <span className="np-chip np-tone-primary">{frameworkCountLabel}</span>
        </div>
        <div className="np-card-body">
          {isLoading ? (
            <div className="d-flex justify-content-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="np-banner np-tone-danger mb-0">
              <i className="ri-error-warning-line"></i>
              <span>Failed to load frameworks.</span>
            </div>
          ) : frameworks.length === 0 ? (
            <p className="np-sub mb-0">No frameworks uploaded yet.</p>
          ) : (
            <NimbusTable thead={<tr>
                    <th>Framework</th>
                    <th>Category</th>
                    <th style={{ width: '120px' }}>Version</th>
                    <th style={{ width: '120px' }}>Controls</th>
                    <th>Description</th>
                    <th style={{ width: '140px' }}>Actions</th>
                  </tr>}>
                  {frameworks.map((fw) => {
                    const controlsCount = fw.controls?.length || 0;
                    const expanded = expandedFrameworkId === fw.frameworkId;
                    return (
                      <React.Fragment key={fw.frameworkId}>
                        <tr>
                          <td>
                            <div className="np-row-title">{fw.name}</div>
                            {fw.code ? <div className="np-sub">{fw.code}</div> : null}
                          </td>
                          <td>
                            <span className="np-chip np-tone-primary">{fw.category}</span>
                          </td>
                          <td className="np-tabular">{fw.version || '-'}</td>
                          <td>
                            <span className="np-chip np-tone-info">{controlsCount} controls</span>
                          </td>
                          <td className="np-sub">{fw.description || '-'}</td>
                          <td>
                            <div className="d-flex align-items-center gap-1">
                              <button
                                className="np-icon-btn"
                                onClick={() => setExpandedFrameworkId(expanded ? null : fw.frameworkId)}
                                title={expanded ? 'Hide' : 'View'}
                              >
                                <i className={expanded ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                              </button>
                              <button
                                className="np-icon-btn np-icon-btn-danger"
                                onClick={() => handleDelete(fw)}
                                disabled={deleteMutation.isPending}
                                title="Delete"
                              >
                                <i className="ri-delete-bin-line"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                        {expanded ? (
                          <tr>
                            <td colSpan={6}>
                              <div className="np-card" style={{ background: 'var(--np-surface-2)' }}>
                                <div className="np-card-head">
                                  <h2>Controls</h2>
                                  <span className="np-sub np-tabular ms-auto">{fw.createdAt ? new Date(fw.createdAt).toLocaleDateString() : ''}</span>
                                </div>
                                <div className="np-card-body pt-0">
                                  <NimbusTable
                                    thead={<tr>
                                      <th style={{ width: '160px' }}>Control ID</th>
                                      <th style={{ width: '320px' }}>Title</th>
                                      <th>Description</th>
                                    </tr>}
                                    isEmpty={(fw.controls || []).length === 0}
                                    emptyState={<p className="np-sub text-center py-3 mb-0">No controls</p>}
                                  >
                                    {(fw.controls || []).map((c) => (
                                      <tr key={c.controlId}>
                                        <td>
                                          <code>{c.controlId}</code>
                                        </td>
                                        <td>{c.title}</td>
                                        <td className="np-sub">{c.description || '-'}</td>
                                      </tr>
                                    ))}
                                  </NimbusTable>
                                </div>
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
            </NimbusTable>
          )}
        </div>
      </div>
    </div>
  );
};
